/* Sign-in / sign-up view. */
import { signInEmail, signUpEmail, signInGoogle, sendPasswordReset } from "../auth.js";
import { OFFLINE_MODE } from "../firebase-config.js";
import { navigate } from "../router.js";

/**
 * After a successful sign-in / sign-up, send the user back to the route
 * they were trying to reach if the admin-gate (or any other auth-aware
 * surface) stashed one in sessionStorage. Falls back to /home.
 *
 * The stashed value is a hash like "#/admin/users". We strip the leading
 * "#/" so navigate() — which prepends "/" itself — gets the right path.
 */
function navigatePostSignIn() {
  let target = "";
  try {
    target = sessionStorage.getItem("sv:postSignInRedirect") || "";
    sessionStorage.removeItem("sv:postSignInRedirect");
  } catch { /* sessionStorage blocked — fine, fall back to /home */ }
  if (target && target.startsWith("#/")) {
    navigate(target.slice(1));   // "#/admin/users" → "/admin/users"
    return;
  }
  navigate("/home");
}

export function renderAuthView(view) {
  let mode = "signin"; // or "signup"

  const draw = () => {
    view.innerHTML = `
      <div class="auth-wrap">
        <p class="auth-back">
          <a href="#/landing">&larr; Back to home</a>
        </p>
        <div class="auth-brand" aria-hidden="true">
          <img class="auth-logo" src="/assets/icons/icon.svg" alt="" width="56" height="56" />
          <span class="auth-wordmark">Study<span class="brand-accent">VET</span></span>
        </div>
        <p class="auth-tagline">Practice. Review. Pass.</p>

      <section class="card auth-card">
        <h1 class="auth-title">${mode === "signin" ? "Welcome back" : "Create your account"}</h1>
        <p class="auth-sub">${mode === "signin"
          ? "Sign in to sync your progress across devices."
          : "Start tracking practice, reviews, and exam scores."}</p>

        ${OFFLINE_MODE ? `
          <div class="notice">
            <strong>Offline mode.</strong> Firebase isn't configured yet, so any email/password works and
            progress is kept locally on this device. Edit <code>js/firebase-config.js</code> to enable sync.
          </div>` : ""}

        <form class="auth-form" id="auth-form">
          ${mode === "signup" ? `
            <label>Name
              <input type="text" name="name" autocomplete="name" required />
            </label>` : ""}
          <label>Email
            <input type="email" name="email" autocomplete="email" required />
          </label>
          <label>Password
            <input type="password" name="password" autocomplete="${mode === "signin" ? "current" : "new"}-password" required minlength="6" />
          </label>
          ${mode === "signin" ? `
            <p class="auth-forgot">
              <a href="#" id="forgot-link">Forgot password?</a>
            </p>` : ""}
          <button type="submit" class="btn btn-primary wide">${mode === "signin" ? "Sign in" : "Create account"}</button>
        </form>

        ${OFFLINE_MODE ? "" : `
          <div class="or">or</div>
          <button id="google-btn" class="btn btn-google wide" type="button" aria-label="Continue with Google">
            <svg class="google-g" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              <path fill="none" d="M0 0h48v48H0z"/>
            </svg>
            <span>Continue with Google</span>
          </button>
        `}

        <p class="auth-toggle">
          ${mode === "signin" ? "New here?" : "Already have an account?"}
          <a href="#" id="toggle-mode">${mode === "signin" ? "Create an account" : "Sign in"}</a>
        </p>
      </section>
      </div>
    `;

    view.querySelector("#toggle-mode").addEventListener("click", (e) => {
      e.preventDefault();
      mode = mode === "signin" ? "signup" : "signin";
      draw();
    });

    view.querySelector("#auth-form").addEventListener("submit", async (e) => {
      e.preventDefault();
      const fd = new FormData(e.target);
      try {
        if (mode === "signin") {
          await signInEmail(fd.get("email"), fd.get("password"));
        } else {
          await signUpEmail(fd.get("email"), fd.get("password"), fd.get("name"));
        }
        navigatePostSignIn();
      } catch (err) {
        window.toast(err.message || "Authentication failed", "bad");
      }
    });

    const gBtn = view.querySelector("#google-btn");
    if (gBtn) {
      gBtn.addEventListener("click", async () => {
        try { await signInGoogle(); navigatePostSignIn(); }
        catch (err) { window.toast(err.message, "bad"); }
      });
    }

    const forgotLink = view.querySelector("#forgot-link");
    if (forgotLink) {
      forgotLink.addEventListener("click", async (e) => {
        e.preventDefault();
        const emailInput = view.querySelector('input[name="email"]');
        const email = (emailInput?.value || "").trim();
        const target = email || (prompt("Email address to send the reset link to:") || "").trim();
        if (!target) return;
        try {
          await sendPasswordReset(target);
          window.toast(`Reset link sent to ${target}. Check your inbox.`, "ok");
        } catch (err) {
          // Firebase returns generic auth/* error codes; map the common ones.
          const code = err?.code || "";
          let msg = err.message || "Couldn't send reset email.";
          if (code === "auth/invalid-email")     msg = "That email address doesn't look right.";
          else if (code === "auth/user-not-found") msg = "No account found with that email.";
          else if (code === "auth/too-many-requests") msg = "Too many attempts. Try again in a minute.";
          window.toast(msg, "bad");
        }
      });
    }
  };

  draw();
}
